import { Routes } from '@front-app/constants/routes';
import { EventV2 } from '@front-app/services/EventsApi/EventModel';
import { Content } from '@front-app/services/GroupsApi/DiscussionModel';
import { Group, Groups } from '@front-app/services/GroupsApi/GroupModel';
import { EMPTY_LARGE_LOGO } from '@front-app/services/ImageApi/ImageModel';
import { PartialOrganization } from '@front-app/services/OrganizationsApi/OrganizationModel';
import { UserSearchItem } from '@front-app/services/UserApi/UserModel';
import { EventsBannerStore, EventsLogoStore } from '@front-app/store/singletons/Events';
import { GroupsLogoStore } from '@front-app/store/singletons/Groups';
import { UserAvatarStore } from '@front-app/store/singletons/User';
import { eventLink } from '@front-app/util/links';
import { SearchResultType } from '@front-app/services/SearchApi/SearchApi';
import {
	computed,
	ref,
	Ref,
	watch,
} from '@vue/composition-api';
import { Location } from 'vue-router';

export const enum SearchSource {
	EVENT,
	GROUP,
	USER,
	FILE,
	ORGANIZATION,
}

export interface SearchOption<O> {
	key: string;
	title: string;
	subtitle: string;
	source: SearchSource | SearchResultType;
	url: string | Location;
	imageUrl: string;
	imageAlt: string;
	fetchImage: () => Promise<void>;
	original: O;
}

export interface UseSearch<O> {
	searchString: Ref<string>;
	results: Ref<SearchOption<O>[]>;
}

/**
 * A hook to simplify searching. Returns a mutable searchString which when changed
 * causes the results array to be filtered as needed by the filter function
 *
 * @param dataSource The original data source to be searched
 * @param filterFn A filter taking in the search query and returning valid results
 * @param mapperFn A function mapping abstract T to a SearchOption
 * @returns UseSearch The searchString, and resultset of SearchOptions
 */
export function useSearch<T>(
	dataSource: Ref<T[]>,
	filterFn: (_: T, searchString: Ref<string>) => boolean,
	mapperFn: (_: T) => SearchOption<T>,
): UseSearch<T> {
	const searchString = ref('');
	const results = computed(() => dataSource.value
		.filter((_) => filterFn(_, searchString))
		.map(mapperFn));
	return {
		searchString,
		results,
	};
}

/**
 * Composes multiple UseSearches from varying sources into a single result-set
 *
 * @param searches An array of UseSearch objects
 * @returns UseSearch the composed UseSearch which will act as expected. No sorting is performed
 */
export function useComposedSearch<T>(searches: UseSearch<T>[]): UseSearch<T> {
	const searchString = ref('');
	const results = computed(() => searches.flatMap((search) => search.results.value));
	watch(searchString, () => {
		searches.forEach((search) => {
			const internalSearchValue = search.searchString;
			internalSearchValue.value = searchString.value;
		});
	});
	return {
		searchString,
		results,
	};
}

/** CONCRETE IMPLEMENTATIONS * */
// Groups
export function useGroupSearch(groups: Ref<Group[]>): UseSearch<Group> {
	return useSearch(groups, groupFilter, groupToSearchOption);
}

function groupFilter(group: Group, searchValue: Ref<string>): boolean {
	return group.name.toLocaleLowerCase().includes(searchValue.value.toLocaleLowerCase());
}

const getGroupLogo = (id: string) => GroupsLogoStore.get(id);

function groupToSearchOption(original: Group): SearchOption<Group> {
	return {
		key: original.id ? `${original.id}_group` : '',
		title: original.name,
		subtitle: 'groups.search.source',
		source: SearchSource.GROUP,
		url: { name: Routes.GROUP_MAIN, params: { groupId: original.id } },
		imageUrl: original.id ? getGroupLogo(original.id).src : '',
		imageAlt: 'groups.search.imageAlt',
		fetchImage: () => GroupsLogoStore.fetch(original.id),
		original,
	};
}

// Events
export function useEventSearch(events: Ref<EventV2[]>): UseSearch<EventV2> {
	return useSearch(events, eventFilter, eventToSearchOption);
}

function eventFilter(event: EventV2, searchValue: Ref<string>): boolean {
	return event.name.toLocaleLowerCase().includes(searchValue.value.toLocaleLowerCase());
}

const getEventLogo = (id: string) => {
	const logo = EventsLogoStore.get(id);
	if (logo.src && logo.src !== EMPTY_LARGE_LOGO) {
		return logo;
	}
	return EventsBannerStore.get(id);
};

const fetchEventLogo = async (id: string): Promise<void> => {
	await EventsLogoStore.fetch(id);
	const logoSrc = EventsLogoStore.get(id)?.src;
	if (logoSrc && logoSrc !== EMPTY_LARGE_LOGO) {
		return;
	}
	await EventsBannerStore.fetch(id);
};

function eventToSearchOption(original: EventV2): SearchOption<EventV2> {
	return {
		original,
		key: original.id ? `${original.id}_event` : '',
		title: original.name,
		subtitle: 'events.search.source',
		source: SearchSource.EVENT,
		url: eventLink(original.id),
		imageUrl: original.id ? getEventLogo(original.id).src : '',
		imageAlt: 'events.search.imageAlt',
		fetchImage: () => fetchEventLogo(original.id),
	};
}

// Users
export function useUserSearch(users: Ref<UserSearchItem[]>): UseSearch<UserSearchItem> {
	return useSearch(users, userFilter, userToSearchOption);
}

export function userFilter(user: UserSearchItem, searchValue: Ref<string>): boolean {
	const testString = searchValue.value.toLocaleLowerCase();
	return [
		user.firstName,
		user.lastName,
		user.middleName,
	]
		.filter(Boolean)
		.map((n) => n.toLocaleLowerCase())
		.some((n) => n.includes(testString));
}

export function userToSearchOption(original: UserSearchItem): SearchOption<UserSearchItem> {
	return {
		original,
		key: original.id ? `${original.id}_user` : '',
		title: '', // Insert user's i18n'd name in the template
		subtitle: 'user.search.source',
		source: SearchSource.USER,
		url: '',
		imageUrl: original.id ? UserAvatarStore.get(original.id).src : '',
		imageAlt: 'user.search.imageAlt',
		fetchImage: () => UserAvatarStore.fetch(original.id),
	};
}

// Everything
export function useGlobalSearch(events: Ref<EventV2[]>, groups: Ref<Groups>): UseSearch<Group | EventV2> {
	return useComposedSearch<EventV2 | Group>([
		useEventSearch(events),
		useGroupSearch(groups),
	]);
}

// File search
export function useFileSearch(content: Ref<Content[]>): UseSearch<Content> {
	return useSearch(content, contentFilter, contentToSearchOption);
}

function contentFilter(content: Content, searchValue: Ref<string>): boolean {
	const testString = searchValue.value.toLocaleLowerCase();
	return content.name.toLocaleLowerCase().includes(testString);
}

function contentToSearchOption(original: Content): SearchOption<Content> {
	return {
		original,
		key: original.id ? `${original.id}_content` : '',
		title: original.name,
		subtitle: '',
		source: SearchSource.FILE,
		url: '',
		imageUrl: '',
		imageAlt: '',
		fetchImage: () => Promise.resolve(),
	};
}

// Organization search
export function useOrganizationSearch(organization: Ref<PartialOrganization[]>): UseSearch<PartialOrganization> {
	return useSearch(organization, organizationFilter, organizationToSearchOption);
}

function organizationFilter(organization: PartialOrganization, searchValue: Ref<string>): boolean {
	const { name } = organization;
	const testString = searchValue.value.toLocaleLowerCase();
	return (name || '').toLocaleLowerCase().includes(testString);
}

function organizationToSearchOption(original: PartialOrganization): SearchOption<PartialOrganization> {
	const baseUrl = process.env.VUE_APP_TRAPP_PUBLIC_URL;
	return {
		original,
		key: original.name ? `${original.id}_org` : '',
		title: original.name || '',
		subtitle: original.type || '',
		source: SearchSource.ORGANIZATION,
		url: '',
		imageUrl: `${baseUrl}${original.logoSrc}.png`,
		imageAlt: '',
		fetchImage: () => Promise.resolve(),
	};
}
